'use client'

import { OperationType, type SafeTransaction } from '@polymarket/builder-relayer-client'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { encodeFunctionData, erc20Abi } from 'viem'
import type { Address, PublicClient, WalletClient } from 'viem'

import { getLegacyBrokenSafe } from '@/constants/legacy-broken-safes'
import { PUSD_CONTRACT_ADDRESS, USDC_E_CONTRACT_ADDRESS } from '@/constants/tokens'
import { createPolymarketRelayClient } from '@/lib/polymarket/wallet-readiness'
import {
	deriveDepositWalletAddress,
	ensureDepositWalletReady,
} from '@/lib/wallets/polymarket-deposit'
import { captureApiError } from '@/utils/sentry-helpers'

const erc20BalanceAbi = [
	{
		name: 'balanceOf',
		type: 'function',
		stateMutability: 'view',
		inputs: [{ name: 'account', type: 'address' }],
		outputs: [{ name: '', type: 'uint256' }],
	},
] as const

export type UpgradeState =
	| 'idle'
	| 'checking'
	| 'deploying'
	| 'awaiting-signature'
	| 'submitting'
	| 'verifying'
	| 'success'
	| 'error'

export interface UseBrokenSafeUpgradeArgs {
	userId: string | null
	eoaAddress: `0x${string}` | undefined
	walletClient: WalletClient | null
	publicClient: PublicClient | null
}

export interface UseBrokenSafeUpgradeResult {
	state: UpgradeState
	error: string | null
	usdceBalance: bigint | null
	pusdBalance: bigint | null
	upgrade: () => Promise<void>
	reset: () => void
}

function buildErc20TransferTx(token: Address, to: Address, amount: bigint): SafeTransaction {
	return {
		to: token,
		value: '0',
		data: encodeFunctionData({
			abi: erc20Abi,
			functionName: 'transfer',
			args: [to, amount],
		}),
		operation: OperationType.Call,
	}
}

/**
 * Build the array of Safe transactions needed to drain the broken Safe.
 * Exported for unit testing — the hook composes this with the relayer call.
 */
export function buildDrainTxs(params: {
	depositWallet: Address
	usdceBalance: bigint
	pusdBalance: bigint
}): SafeTransaction[] {
	const txs: SafeTransaction[] = []
	if (params.usdceBalance > 0n) {
		txs.push(
			buildErc20TransferTx(
				USDC_E_CONTRACT_ADDRESS as Address,
				params.depositWallet,
				params.usdceBalance
			)
		)
	}
	if (params.pusdBalance > 0n) {
		txs.push(
			buildErc20TransferTx(
				PUSD_CONTRACT_ADDRESS as Address,
				params.depositWallet,
				params.pusdBalance
			)
		)
	}
	return txs
}

async function readBalances(
	publicClient: PublicClient,
	safe: Address
): Promise<{ usdce: bigint; pusd: bigint }> {
	const [usdce, pusd] = await Promise.all([
		publicClient.readContract({
			address: USDC_E_CONTRACT_ADDRESS as Address,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [safe],
		}),
		publicClient.readContract({
			address: PUSD_CONTRACT_ADDRESS as Address,
			abi: erc20BalanceAbi,
			functionName: 'balanceOf',
			args: [safe],
		}),
	])
	return { usdce, pusd }
}

export function useBrokenSafeUpgrade(args: UseBrokenSafeUpgradeArgs): UseBrokenSafeUpgradeResult {
	const [state, setState] = useState<UpgradeState>('idle')
	const [error, setError] = useState<string | null>(null)
	const [usdceBalance, setUsdceBalance] = useState<bigint | null>(null)
	const [pusdBalance, setPusdBalance] = useState<bigint | null>(null)

	const queryClient = useQueryClient()

	const reset = useCallback(() => {
		setState('idle')
		setError(null)
	}, [])

	const upgrade = useCallback(async () => {
		setError(null)
		setState('checking')

		const entry = getLegacyBrokenSafe(args.userId)
		if (!(entry && args.eoaAddress && args.walletClient && args.publicClient)) {
			setError('Wallet not ready')
			setState('error')
			return
		}

		const safeAddress = entry.safeAddress
		const depositWallet = deriveDepositWalletAddress(args.eoaAddress)
		const publicClient = args.publicClient
		const walletClient = args.walletClient

		try {
			const balances = await readBalances(publicClient, safeAddress)
			setUsdceBalance(balances.usdce)
			setPusdBalance(balances.pusd)

			// Step 1: deploy + approve + wrap any pre-existing USDC.e on deposit wallet
			setState('deploying')
			await ensureDepositWalletReady({
				walletClient,
				publicClient,
				depositWalletAddress: depositWallet,
			})

			// Short-circuit if both balances on the broken Safe are 0
			if (balances.usdce === 0n && balances.pusd === 0n) {
				setState('success')
				return
			}

			// Build the drain calls (one per non-zero asset)
			const txs = buildDrainTxs({
				depositWallet,
				usdceBalance: balances.usdce,
				pusdBalance: balances.pusd,
			})

			// User signs SafeTx hash via EIP-712 inside RelayClient.execute,
			// relayer pays gas and submits on-chain.
			setState('awaiting-signature')
			const relayer = createPolymarketRelayClient({ walletClient })
			setState('submitting')
			const response = await relayer.execute(txs, 'Migrate broken Safe to deposit wallet')

			setState('verifying')
			await response.wait()

			// Re-run readiness so any USDC.e that just arrived on the deposit
			// wallet gets wrapped to pUSD (idempotent — wrap step skips if zero).
			await ensureDepositWalletReady({
				walletClient,
				publicClient,
				depositWalletAddress: depositWallet,
			})

			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['polymarket'] }),
				queryClient.invalidateQueries({ queryKey: ['portfolio'] }),
				queryClient.invalidateQueries({ queryKey: ['polygonBalances'] }),
				queryClient.invalidateQueries({ queryKey: ['market-balance', 'polymarket'] }),
			])

			setState('success')
		} catch (err) {
			console.error('Broken Safe upgrade failed:', err)
			captureApiError(err, {
				endpoint: 'broken_safe_upgrade',
				extra: {
					userId: args.userId,
					safeAddress,
					depositWallet,
				},
				tags: { category: 'broken_safe_upgrade' },
			})
			setError(err instanceof Error ? err.message : 'Unknown error')
			setState('error')
		}
	}, [args.userId, args.eoaAddress, args.walletClient, args.publicClient, queryClient])

	return { state, error, usdceBalance, pusdBalance, upgrade, reset }
}
